/**
 * Allergies & Dislikes (v3): combined `allergy_dislikes` picker — active when `app_config.allergy_picker_mode` is `combined`.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Alert,
  Pressable,
  ScrollView,
  type ListRenderItem,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Input } from '../components/common/Input';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { MainLoader } from '../components/load-main/MainLoader';
import { SvgIcon } from '../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../components/common/Clickable';
import { useAuthStore } from '../shared/auth/store';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useTranslation } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  studioSectionCardStyle,
  studioSectionLabelStyle,
  androidScaleFont,
  androidFlatSurfaceStyle,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  usePreferencesCatalog as useAllergyDislikesQuery,
  useSavePreferencesMutation as useSaveAllergyDislikesMutation,
  useSelectedInventoryIds,
  refetchCombinedPreferencesCatalog,
} from '../data/domains/preferences';
import { useProfileAllergiesDislikesSync } from '../data/domains/preferences';
import { usePreferencesSelection } from '../data/domains/preferences';
import { PreferencesSelectedChips } from './components/PreferencesSelectedChips';
import { PreferencesItemRow } from './components/PreferencesItemRow';
import { PreferencesCatalogUnavailable } from './components/PreferencesCatalogUnavailable';
import { extractCategoriesFromAllergyDislikeResponse } from '../data/domains/preferences';
import {
  buildPreferencesCatalog,
  buildFlatSubCategoryIndex,
  buildCategoryListRows,
  buildSearchListRows,
  getCategoryItemIds,
  isSubCategoryFullySelected,
  resolveSelectedSubCategoryChips,
  type PreferenceListRow,
} from '../data/domains/preferences';
import { useQueryClient } from '@tanstack/react-query';
import { useFocusEffect } from '@react-navigation/native';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { endpoints } from '../shared/config/endpoints';
import { getHttpStatusFromError } from '../platform/support/issueReport';

const MAX_INVENTORY_SELECTION = 60;
const FOOTER_APPROX_HEIGHT = 148;
const SEARCH_DEBOUNCE_MS = 220;
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<PreferenceListRow>);

function useDebouncedValue<T>(value: T, delay_ms: number): T {
  const [debounced, set_debounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => set_debounced(value), delay_ms);
    return () => clearTimeout(timer);
  }, [value, delay_ms]);

  return debounced;
}

function createAllergiesDislikesStyles(c: ThemeColors) {
  const sf = androidScaleFont;

  return StyleSheet.create({
    body: {
      flex: 1,
    },
    chrome: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
    },
    list_content: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      flexGrow: 1,
    },
    summary_card: {
      marginBottom: appTheme.spacing.lg,
    },
    summary_icon_wrap: {
      width: 40,
      height: 40,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${c.primary}14`,
    },
    summary_title_row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.xs,
    },
    summary_title: {
      ...appTheme.typography.body,
      fontSize: sf(appTheme.typography.body.fontSize ?? 16),
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    summary_hint: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      color: c.textSecondary,
      lineHeight: sf(20),
    },
    progress_track: {
      height: 4,
      borderRadius: 999,
      backgroundColor: `${c.text}08`,
      marginTop: appTheme.spacing.md,
      overflow: 'hidden',
    },
    progress_fill: {
      height: '100%',
      borderRadius: 999,
      backgroundColor: c.primary,
    },
    progress_fill_warning: {
      backgroundColor: c.warning ?? c.primary,
    },
    unsaved_row: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: appTheme.spacing.sm,
    },
    unsaved_badge: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      fontWeight: '700',
      color: c.primary,
    },
    search_wrap: {
      marginBottom: appTheme.spacing.md,
    },
    section_label: {
      marginBottom: appTheme.spacing.sm,
      marginStart: appTheme.spacing.xs,
    },
    category_panel: {
      marginBottom: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xs,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: hexToRgba(c.surface, 0.72),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${c.text}0c`,
      ...androidFlatSurfaceStyle(),
    },
    category_panel_inner: {
      paddingHorizontal: appTheme.spacing.sm,
    },
    category_strip: {
      paddingHorizontal: appTheme.spacing.xs,
      marginBottom: appTheme.spacing.sm,
    },
    category_pill: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: 10,
      borderRadius: 999,
      marginEnd: appTheme.spacing.xs,
      backgroundColor: c.backgroundFade ?? `${c.text}06`,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${c.text}10`,
    },
    category_pill_active: {
      backgroundColor: c.primaryUltra ?? `${c.primary}14`,
      borderColor: c.primary,
    },
    category_pill_label: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      fontWeight: '600',
      color: c.textSecondary,
    },
    category_pill_label_active: {
      color: c.primary,
    },
    category_actions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.sm,
      gap: appTheme.spacing.sm,
    },
    category_actions_title: {
      ...appTheme.typography.body,
      fontSize: sf(appTheme.typography.body.fontSize ?? 16),
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    category_action_btn: {
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: 6,
      borderRadius: 999,
      backgroundColor: `${c.primary}10`,
    },
    category_action_btn_muted: {
      backgroundColor: `${c.text}08`,
    },
    category_action_label: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      fontWeight: '700',
      color: c.primary,
    },
    category_action_label_muted: {
      color: c.textSecondary,
    },
    loaderWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl,
    },
    loaderText: {
      marginTop: appTheme.spacing.sm,
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      color: c.textSecondary,
    },
    emptyWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl,
    },
    emptySub: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      marginTop: appTheme.spacing.sm,
      textAlign: 'center',
      color: c.textSecondary,
    },
    footer: {
      flexShrink: 0,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: `${c.text}14`,
      backgroundColor: c.background,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      ...androidFlatSurfaceStyle(),
    },
    footer_meta: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: sf(18),
      marginBottom: appTheme.spacing.sm,
    },
    footer_meta_warning: {
      color: c.warning ?? c.primary,
      fontWeight: '600',
    },
    footer_actions: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.md,
      width: '100%',
    },
    footer_btn_outer: {
      flex: 1,
      minWidth: 0,
    },
    footer_btn_content: {
      alignSelf: 'stretch',
      width: '100%',
    },
  });
}

export const AllergiesDislikesScreenV3: React.FC = () => {
  const { t } = useTranslation();
  const rtl = useRtl();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  useRequireVerified('AllergiesDislikes');
  const theme_colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAllergiesDislikesStyles(theme_colors), [theme_colors]);
  const summary_card_style = useMemo(
    () => studioSectionCardStyle(theme_colors, { padding: appTheme.spacing.lg, marginBottom: 0 }),
    [theme_colors],
  );
  const section_label_style = useMemo(() => studioSectionLabelStyle(theme_colors), [theme_colors]);
  const header_top_pad = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const cancel_secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(theme_colors),
      label: createStackedFooterSecondaryLabelStyle(theme_colors),
    }),
    [theme_colors],
  );
  const save_primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(theme_colors),
      label: createStackedFooterPrimaryLabelStyle(theme_colors),
    }),
    [theme_colors],
  );

  const customer_id = useAuthStore(s => s.user?.id);
  const query_client = useQueryClient();
  const [search_query, set_search_query] = useState('');
  const [active_category_id, set_active_category_id] = useState<number | null>(null);
  const debounced_search = useDebouncedValue(search_query, SEARCH_DEBOUNCE_MS);
  const toggle_handlers_ref = useRef<Map<string, () => void>>(new Map());
  const selection_api_ref = useRef<ReturnType<typeof usePreferencesSelection> | null>(null);

  const saved_inventory_ids = useSelectedInventoryIds();
  const { isLoading: loading_profile } = useProfileAllergiesDislikesSync(customer_id);

  const {
    data: allergy_dislikes_data,
    isLoading: loading_allergy_dislikes,
    isError: allergy_dislikes_load_error,
    error: allergy_dislikes_error,
  } = useAllergyDislikesQuery(customer_id);

  /** Force refresh when opening combined allergies page (overrides 10m launch cache). */
  useFocusEffect(
    useCallback(() => {
      if (typeof customer_id !== 'number') {
        return;
      }
      void refetchCombinedPreferencesCatalog(query_client, customer_id);
    }, [customer_id, query_client]),
  );

  const save_inventory = useSaveAllergyDislikesMutation();

  const catalog = useMemo(() => {
    const raw = extractCategoriesFromAllergyDislikeResponse(allergy_dislikes_data);
    return buildPreferencesCatalog(raw);
  }, [allergy_dislikes_data]);

  const flat_index = useMemo(() => buildFlatSubCategoryIndex(catalog), [catalog]);

  const selection = usePreferencesSelection(saved_inventory_ids, MAX_INVENTORY_SELECTION);
  const {
    version: selection_version,
    selected_count,
    selected_ids_array,
    has_unsaved_changes,
    discard,
    get_ids_for_save,
  } = selection;
  selection_api_ref.current = selection;

  useEffect(() => {
    toggle_handlers_ref.current.clear();
  }, [catalog]);

  useEffect(() => {
    if (catalog.length === 0) {
      set_active_category_id(null);
      return;
    }
    set_active_category_id(prev => {
      if (prev != null && catalog.some(category => category.id === prev)) {
        return prev;
      }
      return catalog[0].id;
    });
  }, [catalog]);

  const is_search_mode = debounced_search.trim().length > 0;

  const active_category = useMemo(
    () => catalog.find(category => category.id === active_category_id) ?? null,
    [catalog, active_category_id],
  );

  const list_rows = useMemo(() => {
    if (is_search_mode) {
      return buildSearchListRows(flat_index, debounced_search);
    }
    if (!active_category) {
      return [];
    }
    return buildCategoryListRows(active_category);
  }, [active_category, debounced_search, flat_index, is_search_mode]);

  const selected_chip_items = useMemo(
    () => resolveSelectedSubCategoryChips(flat_index, selected_ids_array),
    [flat_index, selected_ids_array],
  );

  const notify_limit_reached = useCallback(() => {
    Alert.alert(
      t('account.maxSelectionsTitle'),
      t('account.maxSelectionsMessage', { max: MAX_INVENTORY_SELECTION }),
    );
  }, [t]);

  const handle_toggle_sub_category = useCallback(
    (item_ids: number[]) => {
      if (item_ids.length === 0) {
        return;
      }
      const all_selected = item_ids.every(id => selection_api_ref.current?.is_selected(id));
      if (all_selected) {
        selection_api_ref.current?.set_ids(item_ids, 'remove');
        return;
      }
      const result = selection_api_ref.current?.set_ids(item_ids, 'add');
      if (result === 'limit') {
        notify_limit_reached();
      }
    },
    [notify_limit_reached],
  );

  const handle_remove_chip = useCallback((item_ids: number[]) => {
    if (item_ids.length === 0) {
      return;
    }
    selection_api_ref.current?.set_ids(item_ids, 'remove');
  }, []);

  const get_toggle_handler = useCallback(
    (row_key: string, item_ids: number[]) => {
      let handler = toggle_handlers_ref.current.get(row_key);
      if (!handler) {
        handler = () => handle_toggle_sub_category(item_ids);
        toggle_handlers_ref.current.set(row_key, handler);
      }
      return handler;
    },
    [handle_toggle_sub_category],
  );

  const handle_select_all_category = useCallback(() => {
    if (!active_category) {
      return;
    }
    const result = selection_api_ref.current?.set_ids(getCategoryItemIds(active_category), 'add');
    if (result === 'limit') {
      notify_limit_reached();
    }
  }, [active_category, notify_limit_reached]);

  const handle_clear_category = useCallback(() => {
    if (!active_category) {
      return;
    }
    selection_api_ref.current?.set_ids(getCategoryItemIds(active_category), 'remove');
  }, [active_category]);

  const handle_discard = useCallback(() => {
    discard();
  }, [discard]);

  const selection_at_limit = selected_count >= MAX_INVENTORY_SELECTION;

  const handle_save = useCallback(() => {
    if (selection_at_limit) {
      Alert.alert(t('account.maxSelectionsTitle'), t('account.preferencesOverLimitSaveHint'));
      return;
    }
    if (!has_unsaved_changes || save_inventory.isPending) {
      return;
    }
    save_inventory.mutate(get_ids_for_save(), {
      onSuccess: () => {
        Alert.alert(t('common.success'), t('account.preferencesSaved'));
      },
      onError: () => {
        Alert.alert(t('common.error'), t('account.preferencesSaveFailed'));
      },
    });
  }, [get_ids_for_save, has_unsaved_changes, save_inventory, selection_at_limit, t]);

  const progress_ratio = Math.min(selected_count / MAX_INVENTORY_SELECTION, 1);
  const save_disabled = !has_unsaved_changes || save_inventory.isPending || selection_at_limit;
  const footer_hint = selection_at_limit
    ? t('account.preferencesOverLimitSaveHint')
    : has_unsaved_changes
      ? t('account.savePreferencesHint')
      : null;

  const selected_ids_set = useMemo(() => new Set(selected_ids_array), [selected_ids_array]);

  const render_row: ListRenderItem<PreferenceListRow> = useCallback(
    ({ item }) => {
      const sub_item_ids = item.sub_category_item_ids;
      return (
        <PreferencesItemRow
          colors={theme_colors}
          title={item.title}
          breadcrumb={item.breadcrumb}
          selected={isSubCategoryFullySelected(sub_item_ids, selected_ids_set)}
          onPress={get_toggle_handler(item.key, sub_item_ids)}
        />
      );
    },
    [get_toggle_handler, selected_ids_set, selection_version, theme_colors],
  );

  const list_empty = useMemo(() => {
    if (catalog.length === 0) {
      return (
        <View style={styles.emptyWrap}>
          <SvgIcon name="medkit" size={48} color={theme_colors.textLight} />
          <Text style={styles.emptySub}>{t('account.noAllergiesList')}</Text>
        </View>
      );
    }
    if (list_rows.length === 0) {
      return (
        <View style={styles.emptyWrap}>
          <SvgIcon name="search" size={40} color={theme_colors.textLight} />
          <Text style={styles.emptySub}>{t('account.noSearchResults')}</Text>
        </View>
      );
    }
    return null;
  }, [catalog.length, list_rows.length, styles.emptySub, styles.emptyWrap, t, theme_colors.textLight]);

  const loading_page = loading_profile || loading_allergy_dislikes;
  const page_title = t('account.allergiesAndDislikes');
  const allergy_dislikes_api_endpoint = `GET ${endpoints.allergy_dislikes}?customer_id=${customer_id ?? ''}`;
  const list_bottom_pad = FOOTER_APPROX_HEIGHT + insets.bottom + appTheme.spacing.lg;
  const footer_bottom_pad = Math.max(insets.bottom, appTheme.spacing.md);

  return (
    <StackScreenShell colors={theme_colors}>
      <ScreenHeader title={page_title} onBack={on_back_exit} scrollY={scrollY} />

      <View style={[styles.body, { paddingTop: header_top_pad }]}>
        {loading_page ? (
          <View style={styles.loaderWrap}>
            <MainLoader size={48} color={theme_colors.primary} />
            <Text style={styles.loaderText}>{t('common.loading')}</Text>
          </View>
        ) : allergy_dislikes_load_error ? (
          <View style={styles.list_content}>
            <PreferencesCatalogUnavailable
              colors={theme_colors}
              page_title={page_title}
              tab_label={page_title}
              issue="Allergies & dislikes failed to load from server"
              api_endpoint={allergy_dislikes_api_endpoint}
              customer_id={customer_id}
              http_status={getHttpStatusFromError(allergy_dislikes_error)}
              error={allergy_dislikes_error}
            />
          </View>
        ) : (
          <>
            <View style={styles.chrome}>
              <View style={[summary_card_style, styles.summary_card]}>
                <View style={styles.summary_title_row}>
                  <View style={styles.summary_icon_wrap}>
                    <SvgIcon name="medkit" size={20} color={theme_colors.primary} />
                  </View>
                  <Text style={styles.summary_title}>{t('account.preferencesPickerTitle')}</Text>
                </View>
                <Text style={styles.summary_hint}>{t('account.preferencesPickerHint')}</Text>
                <View style={styles.progress_track}>
                  <View
                    style={[
                      styles.progress_fill,
                      selection_at_limit && styles.progress_fill_warning,
                      { width: `${Math.max(progress_ratio * 100, selection_at_limit ? 100 : 2)}%` },
                    ]}
                  />
                </View>
                {has_unsaved_changes ? (
                  <View style={styles.unsaved_row}>
                    <Text style={styles.unsaved_badge}>{t('account.unsavedChanges')}</Text>
                  </View>
                ) : null}
              </View>

              <View style={styles.search_wrap}>
                <Input
                  value={search_query}
                  onChangeText={set_search_query}
                  placeholder={t('common.search')}
                  icon="search"
                  iconPosition="start"
                  containerStyle={{ marginBottom: 0 }}
                />
              </View>

              <PreferencesSelectedChips
                colors={theme_colors}
                items={selected_chip_items}
                onRemove={handle_remove_chip}
              />

              {!is_search_mode && catalog.length > 0 ? (
                <View style={styles.category_panel}>
                  <View style={styles.category_panel_inner}>
                    <Text style={[section_label_style, styles.section_label]}>{t('account.preferencesCategoriesLabel')}</Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      keyboardShouldPersistTaps="handled"
                      contentContainerStyle={styles.category_strip}>
                      {catalog.map((category, category_index) => {
                        const active = category.id === active_category_id;

                        return (
                          <Pressable
                            key={`category-pill-${category.id}-${category_index}`}
                            onPress={() => set_active_category_id(category.id)}
                            style={({ pressed }) => [
                              styles.category_pill,
                              active && styles.category_pill_active,
                              pressed && { opacity: 0.92 },
                            ]}
                            accessibilityRole="button"
                            accessibilityState={{ selected: active }}>
                            <Text
                              style={[styles.category_pill_label, active && styles.category_pill_label_active]}
                              numberOfLines={1}>
                              {category.title}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </ScrollView>

                    {active_category ? (
                      <View style={styles.category_actions}>
                        <Text style={styles.category_actions_title} numberOfLines={1}>
                          {active_category.title}
                        </Text>
                        <Pressable
                          onPress={handle_select_all_category}
                          style={({ pressed }) => [styles.category_action_btn, pressed && { opacity: 0.92 }]}>
                          <Text style={styles.category_action_label}>{t('account.selectAllInCategory')}</Text>
                        </Pressable>
                        <Pressable
                          onPress={handle_clear_category}
                          style={({ pressed }) => [
                            styles.category_action_btn,
                            styles.category_action_btn_muted,
                            pressed && { opacity: 0.92 },
                          ]}>
                          <Text style={[styles.category_action_label, styles.category_action_label_muted]}>
                            {t('account.clearCategory')}
                          </Text>
                        </Pressable>
                      </View>
                    ) : null}
                  </View>
                </View>
              ) : null}
            </View>

            <AnimatedFlatList
              style={styles.body}
              data={list_rows}
              onScroll={onScroll}
              scrollEventThrottle={16}
              key={is_search_mode ? 'search' : `category-${active_category_id ?? 'none'}`}
              keyExtractor={item => item.key}
              renderItem={render_row}
              ListEmptyComponent={list_empty}
              contentContainerStyle={[styles.list_content, { paddingBottom: list_bottom_pad }]}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              keyboardDismissMode="on-drag"
              initialNumToRender={16}
              maxToRenderPerBatch={14}
              windowSize={9}
              removeClippedSubviews={false}
              extraData={selection_version}
            />

            <View style={[styles.footer, { paddingBottom: footer_bottom_pad }]}>
              {footer_hint ? (
                <Text
                  style={[
                    styles.footer_meta,
                    selection_at_limit && styles.footer_meta_warning,
                    { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                  ]}>
                  {footer_hint}
                </Text>
              ) : null}

              <View style={styles.footer_actions}>
                {has_unsaved_changes ? (
                  <Clickable
                    onPress={handle_discard}
                    disabled={save_inventory.isPending}
                    accessibilityLabel={t('account.discardChanges')}
                    accessibilityRole="button"
                    useNativePressable
                    haptics
                    pressScale={PRESS_PRESETS.listRow.scale}
                    contentBorderRadius={appTheme.borderRadius.lg}
                    style={styles.footer_btn_outer}
                    contentStyle={styles.footer_btn_content}>
                    <View style={[cancel_secondary_cta.surface, save_inventory.isPending && { opacity: 0.55 }]}>
                      <Text style={cancel_secondary_cta.label}>{t('common.cancel')}</Text>
                    </View>
                  </Clickable>
                ) : null}
                <Clickable
                  onPress={handle_save}
                  disabled={save_disabled}
                  accessibilityLabel={t('account.savePreferences')}
                  accessibilityRole="button"
                  useNativePressable
                  haptics
                  hapticType="impactLight"
                  contentBorderRadius={appTheme.borderRadius.lg}
                  style={[styles.footer_btn_outer, !has_unsaved_changes && { flex: 1 }]}
                  contentStyle={styles.footer_btn_content}>
                  <View style={[save_primary_cta.surface, save_disabled && { opacity: 0.5 }]}>
                    {save_inventory.isPending ? (
                      <ButtonLoader size={18} color={theme_colors.onPrimary} />
                    ) : (
                      <Text style={save_primary_cta.label}>{t('account.savePreferences')}</Text>
                    )}
                  </View>
                </Clickable>
              </View>
            </View>
          </>
        )}
      </View>
    </StackScreenShell>
  );
};

export default AllergiesDislikesScreenV3;
